import React, { useRef, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ModalContext } from '$contexts/modal.jsx';
import { useCursorContext } from '$contexts/cursor.jsx';
import { ArrowRightIcon, CloseIcon } from '$partials/icon';
import { Button } from '$partials/ui';

import Util from '$utilities';
import { getLocation } from '$utilities/location';
import { formatPageTitle, formatPageUri } from '$utilities/format';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').BookViewingModal}
 */
const BookViewingModal = ({
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    buildingName,
    buildingNumber,
    postcode,
    contactForm = { hubspotFormId: '764179ab-43b0-4eaf-a73f-144442f50537' },
    hubspot = {},
    ...props
}) => {

    const { handleModal } = useContext(ModalContext);
    const { dispatch: cursorDispatch } = useCursorContext();
    const exitButtonRef = useRef(null);

    const propertyType = () => {
        switch(props.__typename){
            case 'PartnershipPropertyPage':
                return 'NYC Sale Property';
            case 'PartnershipRentalPropertyPage':
                return 'NYC Rental Property';
            case 'PropertyPage':
                return 'Sale Property';
            case 'RentalPropertyPage':
                return 'Rental Property';
        }
    };

    // Form
    const textareaRef = useRef(null);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ height, setHeight ] = useState('');
    const [ message, setMessage ] = useState('');
    const [ hasSubmitted, setHasSubmitted ] = useState(false);
    const {
        formState: { errors },
        handleSubmit,
        register,
        reset,
    } = useForm();
    const { ref, ...rest } = register('address');

    useEffect(() => {
        const exit = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: exitButtonRef,
                cursorType: 'exit'
            },
        });

        return () => [exit].map(cleanup => {
            cleanup();});
    }, []);

    const submit = async (data) => {
        try {
            const response = await fetch('/api/crm/v1/form', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Util.cookies.getItem('crumb')
                },
                body: JSON.stringify({
                    formId: contactForm?.hubspotFormId,
                    fields: [{
                        objectTypeId: '0-1',
                        name: 'email',
                        value : data?.email
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'firstname',
                        value : data?.firstname
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'lastname',
                        value : data?.lastname
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'phone',
                        value : data?.phone
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'address',
                        value : data?.address
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'building_name',
                        value : buildingName
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'building_number',
                        value : buildingNumber
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'address_line_1',
                        value : addressLine1
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'address_line_2',
                        value : addressLine2
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'address_line_3',
                        value : addressLine3
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'address_line_4',
                        value : `${addressLine4} ${postcode}`
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'notes',
                        value : `${propertyType()} (${props.officeId}) ${getLocation().href}`
                    }],
                    context:  {
                        hutk: window.sessionStorage.getItem('hubspot_hubspotutk') || undefined,
                        pageUri: formatPageUri(),
                        pageName: formatPageTitle(props)
                    }
                })
            });

            const result = await response.json();

            if (response.status !== 200) {
                if (result && result.message) {
                    setMessage(result.message || 'Thank you for your booking');
                    setHasSubmitted(true);
                    return;
                } else {
                    throw new Error(response.status);
                }
            }
            setHasSubmitted(true);
            setMessage(result?.message || 'Thank you for your booking');
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setMessage('Sorry, something went wrong, please try again later.');
        }
    };

    const handleInputHeight = () => {
        if (textareaRef.current){
            setHeight(textareaRef.current.scrollHeight);
        }
    };

    return (
        <>
            <div
                className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-black mix-blend-multiply z3 cursor-none'}
                onClick={() => {
                    handleModal();
                    reset();
                }}
                ref={exitButtonRef}
                style={{opacity: '0.2'}}>
            </div>
            <div
                className='height-100 bg-white z4 fixed top-0 overflow-auto modal-container'>
                <button
                    aria-label='Close'
                    className='form-field-reset link-reset absolute right-0 right mt6 mr6 cursor-pointer'
                    onClick={() => {
                        handleModal();
                        reset();
                    }}
                    type='button'>
                    <CloseIcon/>
                </button>
                <div className='py7 px5 md-px6'>
                    <div
                        className='rich-text type-style-2'
                        dangerouslySetInnerHTML={{ __html: hasSubmitted ? message : 'Book A Viewing'}} />
                    <p className='pt5 pb7 type-style-6'>
                        {hasSubmitted ? '' : 'Fill in your details below to request a viewing and we\'ll get back to you to arrange it as soon as possible.'}
                    </p>

                    {hasSubmitted ? (
                        <Button
                            aria-label='Submit'
                            as='button'
                            className = 'bg-black white type-style-5 items-center'
                            look = 'fill'
                            onClick={() => {
                                handleModal();
                                reset();
                            }}
                            renderIcon={<ArrowRightIcon />}>
                                Close
                        </Button>
                    ) : (
                        <form
                            autoComplete='off'
                            data-analytics='book-viewing'
                            data-form-id={contactForm?.hubspotFormId}
                            data-portal-id={hubspot.portalId}
                            onSubmit={handleSubmit(submit)}>
                            <div className='flex flex-wrap mxn5 mb7'>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            Email
                                        </span>
                                        <input
                                            {...register('email', { required: 'Please enter your email.' })}
                                            className={`border-top border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['firstname']?.message ? '' : ' border-bottom'}${errors?.['email']?.message ? ' border red' : ' black'}`}
                                            id='frm-email'/>
                                    </div>
                                    {errors?.['email']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['email']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            First name
                                        </span>
                                        <input
                                            {...register('firstname', { required: 'Please enter your first name.' })}
                                            className={`border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['email']?.message ? ' border-top' : ''}${errors?.['lastname']?.message ? '' : ' border-bottom'}${errors?.['firstname']?.message ? ' border red' : ' black'}`}
                                            id='frm-firstname'/>
                                    </div>
                                    {errors?.['firstname']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['firstname']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            Last name
                                        </span>
                                        <input
                                            {...register('lastname', { required: 'Please enter your last name.' })}
                                            className={`border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['firstname']?.message ? ' border-top' : ''}${errors?.['phone']?.message ? '' : ' border-bottom'}${errors?.['lastname']?.message ? ' border red' : ' black'}`}
                                            id='frm-lastname'/>
                                    </div>
                                    {errors?.['lastname']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['lastname']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 pointer-events-none'>
                                            Phone Number
                                        </span>
                                        <input
                                            {...register('phone', { required: 'Please enter your phone number.' })}
                                            className={`number-input border-left border-right width-100 pr6 pt7 pb6 md-py8 type-style-3 pl5 md-pl6${errors?.['lastname']?.message ? ' border-top' : ''}${errors?.['address']?.message ? '' : ' border-bottom'}${errors?.['phone']?.message ? ' border red' : ' black'}`}
                                            id='frm-phone'
                                            type='number'/>
                                    </div>
                                    {errors?.['phone']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['phone']?.message}
                                        </p>
                                    )}
                                </div>
                                <div className='col-12 px5 '>
                                    <div className='flex width-100 items-center relative'>
                                        <span className='absolute pl5 md-pl6 pb7 md-pb8 top-0 pt2 mt5 pointer-events-none'>
                                            Current Address
                                        </span>
                                        <textarea
                                            {...rest}
                                            className={`border-left border-right border-bottom width-100 type-style-3 pt7 pb6 md-py8 pr6 pl5 md-pl6${errors?.['phone']?.message ? ' border-top' : ''}${errors?.['address']?.message ? ' border-top red' : ' black'}`}
                                            id='frm-address'
                                            name='address'
                                            onChange={() => handleInputHeight()}
                                            ref={(e) => {
                                                ref(e);
                                                textareaRef.current = e;
                                            }}
                                            rows={1}
                                            style={{ height: height ? height + 'px' : 'auto'}}/>
                                    </div>
                                    {errors?.['address']?.message && (
                                        <p className='my5 red width-100'>
                                            {errors?.['address']?.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Button
                                    aria-label='Submit'
                                    as='button'
                                    className = 'bg-black white type-style-5 items-center'
                                    disabled={isLoading}
                                    look = 'fill'
                                    renderIcon={<ArrowRightIcon />}
                                    type='submit'>
                                    Book Viewing
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default BookViewingModal ;
